/** Typed Dataflow Contract
 * (VISTOERA_PRODUCTION_CANVAS_TYPED_DATAFLOW_TEXT_ASSET_LINEAGE_HIGHLIGHT_AND_TOOL_REORDER_P0
 * §1–§8/§20–§22).
 *
 * Formalizes what a Canvas connection actually MEANS: `SOURCE OUTPUT →
 * TARGET INPUT`. Before this module, connection legality lived entirely
 * in `CanvasBoard.tsx`'s `isConnectionAllowed` as a small pile of
 * node-TYPE-PAIR special cases (`if source==IMAGE_ASSET && target==...`).
 * That is exactly the "组合爆炸" pattern §4 forbids, and it is also the
 * literal root cause of this round's two reported bugs:
 *   1. `[生成图片] → [图片/素材]` was ALLOWED (nothing in the old gate ever
 *      rejected a generation node feeding into an Asset node's target
 *      handle — only a narrow `RAW_INPUT_TYPES` allowlist existed, and
 *      `image_asset` was never a member of it).
 *   2. `[生成图片 A] → [生成图片 B]` was REJECTED (the old gate's ONLY rule
 *      for the `image-ref-in` handle was `sourceType !== 'image_asset' →
 *      reject`, so a generation node's own real IMAGE output — which
 *      obviously satisfies "an IMAGE flowing into an IMAGE-accepting
 *      input" — could never pass it).
 *
 * This module replaces BOTH special cases with one general rule,
 * computed from each node type's declared `outputs[]`/accepted input
 * `dataTypes[]` (§4's "source.outputs[] / target.inputs[]" contract):
 * a connection is legal iff the target node type accepts ANY input at
 * all, AND at least one of the source node type's output data types is
 * in the target's accepted set.
 *
 * Deliberately NOT a new persisted field/table (§19 "不要为了字段名字重复建
 * 模型") — `CanvasNodeDto.node_type` + `CanvasEdgeDto.source_handle`/
 * `target_handle` (both already exist, both already persisted,
 * unchanged by this round) are 100% sufficient to derive every one of
 * these types at render/validate time. Old persisted Canvas data (any
 * `node_type`/handle-id combination already on disk) is therefore
 * completely unaffected by this file merely existing — see
 * `getNodeIO`'s explicit fallback for unknown node types below. */

import type { TFunction } from 'i18next'
import { isScriptSourceInputHandle } from './scriptSourceContext'

export type DataType = 'IMAGE' | 'TEXT' | 'VIDEO' | 'AUDIO' | 'VOICE_TEMPLATE' | 'MOTION_ASSET'

export function dataTypeLabel(t: TFunction<'canvas'>, dataType: DataType): string {
  const map: Record<DataType, string> = {
    IMAGE: t('dataflow.dataTypeImage'),
    TEXT: t('dataflow.dataTypeText'),
    VIDEO: t('dataflow.dataTypeVideo'),
    AUDIO: t('dataflow.dataTypeAudio'),
    VOICE_TEMPLATE: t('dataflow.dataTypeVoiceTemplate'),
    MOTION_ASSET: t('dataflow.dataTypeMotionAsset'),
  }
  return map[dataType]
}

/** @deprecated Use dataTypeLabel(t, type) — kept for transitional imports */
export const DATA_TYPE_LABEL: Record<DataType, string> = {
  IMAGE: 'IMAGE',
  TEXT: 'TEXT',
  VIDEO: 'VIDEO',
  AUDIO: 'AUDIO',
  VOICE_TEMPLATE: 'VOICE_TEMPLATE',
  MOTION_ASSET: 'MOTION_ASSET',
}

/** Input Role contract (§20) — reserved for future Virtual IP /
 * VIDEO_KEYFRAME_DESIGN reference-role UI (identity vs. style vs.
 * environment reference pickers). Stored on `CanvasEdgeDto`'s existing
 * open `data`/`relation_type` surface only when actually set — P0 never
 * shows a role selector to normal users (§20 "P0 不要求现在给普通用户展示
 * 所有高级 Role selector"), it only needs to not be blocked at the data
 * -model level, which a plain string union costs nothing to reserve. */
export type InputRole =
  | 'IDENTITY_REFERENCE'
  | 'STYLE_REFERENCE'
  | 'ENVIRONMENT_REFERENCE'
  | 'COMPOSITION_REFERENCE'
  | 'CONTENT_REFERENCE'
  | 'GENERAL_CONTEXT'

export const DEFAULT_INPUT_ROLE: InputRole = 'GENERAL_CONTEXT'

/** The canonical "generic input" target handle id. Kept as the EXACT
 * same wire id `generate_image`/`generate_video` already persist
 * (`image-ref-in`) rather than a renamed `in`/`generic-in` — §19 "不要为
 * 了字段名字重复建模型", and renaming a `<Handle id>` that already has
 * live persisted edges pointing at it would silently break those edges'
 * on-screen rendering (React Flow matches edges to DOM handles by exact
 * id). The id string is legacy; its ACCEPTED data types are not — see
 * `getNodeIO` below, which is what actually generalizes it to accept
 * TEXT/VIDEO/AUDIO, not just IMAGE. */
export const GENERIC_INPUT_HANDLE = 'image-ref-in'

/** Canvas Unified Video Generation Node P0 (spec §7–§9) — the three
 * deterministic KEYFRAME drop targets, distinct DOM `<Handle>` ids on
 * `generate_video` ONLY (never a generic-input guess, spec §8 "只有明确
 * drop 到 START/MIDDLE/END 才形成 Keyframe semantic"). A connection into
 * any of these three is validated by the EXACT SAME `validateTypedConnection`
 * rule as the generic input handle (a keyframe is structurally still
 * "an IMAGE flowing into an IMAGE-accepting input", spec §6 "KEYFRAME
 * 本质仍然是 IMAGE asset + deterministic temporal role") — no separate
 * validation path needed, only a separate persisted `target_handle`
 * string CanvasBoard groups differently once the edge exists.
 * MIDDLE is a single UI slot this round (spec §9) but the backend
 * `KeyframeRef`/`validate_keyframes` contract stays array-based, so this
 * handle id is deliberately singular/reusable rather than baking in an
 * index. */
export const VIDEO_KEYFRAME_START_HANDLE = 'video-kf-start-in'
export const VIDEO_KEYFRAME_MIDDLE_HANDLE = 'video-kf-middle-in'
export const VIDEO_KEYFRAME_END_HANDLE = 'video-kf-end-in'
export const VIDEO_KEYFRAME_HANDLES = [
  VIDEO_KEYFRAME_START_HANDLE,
  VIDEO_KEYFRAME_MIDDLE_HANDLE,
  VIDEO_KEYFRAME_END_HANDLE,
] as const
export type VideoKeyframeHandle = (typeof VIDEO_KEYFRAME_HANDLES)[number]

/** Mirrors the backend's `video_generation_params.ROLE_START_IMAGE` /
 * `ROLE_MIDDLE_IMAGE` / `ROLE_END_IMAGE` string constants verbatim (never
 * a second renamed copy) — see that module's own `KEYFRAME_ROLES`. */
export const VIDEO_KEYFRAME_HANDLE_TO_ROLE: Record<VideoKeyframeHandle, 'start_image' | 'middle_image' | 'end_image'> = {
  [VIDEO_KEYFRAME_START_HANDLE]: 'start_image',
  [VIDEO_KEYFRAME_MIDDLE_HANDLE]: 'middle_image',
  [VIDEO_KEYFRAME_END_HANDLE]: 'end_image',
}

/** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4/§40 —
 * generalizes the single fixed MIDDLE handle above into an arbitrary-N
 * "Keyframe Timeline" without any backend/DB change: `CanvasEdge.target_handle`
 * is already a free-form persisted VARCHAR (see
 * `backend/app/saas/models/production_canvas.py::CanvasEdge`), so encoding
 * a stable `KeyframeTimelineEntry.id` as a suffix on the existing MIDDLE
 * handle id (`video-kf-middle-in::<entryId>`) is 100% additive/persisted —
 * no migration, no parallel edge-metadata table. START/END stay singular
 * (spec §15: at most one START, one END) and keep their exact legacy
 * handle ids unchanged so pre-existing edges keep rendering identically. */
export const VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX = `${VIDEO_KEYFRAME_MIDDLE_HANDLE}::`

export function videoKeyframeMiddleHandleId(entryId: string): string {
  return `${VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX}${entryId}`
}

/** Canonical slot-aware targetHandle for a KeyframeTimelineEntry (P0_V3). */
export function videoKeyframeTargetHandleForEntry(entry: { id: string; role: 'start' | 'middle' | 'end' }): string {
  if (entry.role === 'start') return VIDEO_KEYFRAME_START_HANDLE
  if (entry.role === 'end') return VIDEO_KEYFRAME_END_HANDLE
  return videoKeyframeMiddleHandleId(entry.id)
}

/** Returns the `KeyframeTimelineEntry.id` a MIDDLE handle id was minted
 * for, or `null` for the legacy bare `video-kf-middle-in` handle (pre-
 * existing edges created before this round — treated as one implicit
 * legacy MIDDLE entry, never dropped). */
export function parseVideoKeyframeMiddleEntryId(handle: string | null | undefined): string | null {
  if (!handle) return null
  if (!handle.startsWith(VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX)) return null
  return handle.slice(VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX.length) || null
}

export function isVideoKeyframeHandle(handle: string | null | undefined): boolean {
  if (!handle) return false
  if (handle === VIDEO_KEYFRAME_START_HANDLE || handle === VIDEO_KEYFRAME_END_HANDLE) return true
  if (handle === VIDEO_KEYFRAME_MIDDLE_HANDLE) return true
  return handle.startsWith(VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX)
}

/** Canonical Structured Asset Input Ports
 * (VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
 * §P6/§P7/§P9/§P10/§P19) — four dedicated `generate_video` TARGET
 * handles, one per real UI section (角色/产品/场景/其他参考), each scoped
 * to a specific `KeyframeTimelineEntry.id` via the EXACT SAME free-form
 * `<base>::<entryId>` suffix trick `VIDEO_KEYFRAME_MIDDLE_HANDLE_PREFIX`
 * already established above — never a second encoding scheme. The bare
 * (no-suffix) id is reserved for the legacy/global case (no
 * `keyframeTimeline` yet — `VideoInputAssetsSection`'s global-mode
 * instance), matching `inputAssetRefsForKeyframe`'s own read-time
 * fallback semantics (spec §P19 "不能重新退回...global assets" — this is
 * the ONE pre-existing legacy shape, not a new global tier).
 *
 * `object` keeps its existing `videoKeyframeTimeline.ts`
 * `InputAssetCategory` id (matches `VideoInputAssetsSection`'s own
 * category key) while presenting as the user-facing "product-in" port
 * id — never two different category vocabularies for the same concept. */
export type VideoStructuredAssetCategory = 'character' | 'object' | 'scene' | 'reference'

export const VIDEO_STRUCTURED_ASSET_HANDLE_BASE: Record<VideoStructuredAssetCategory, string> = {
  character: 'character-in',
  object: 'product-in',
  scene: 'scene-in',
  reference: 'reference-asset-in',
}

export function videoStructuredAssetHandleId(
  category: VideoStructuredAssetCategory,
  keyframeId?: string | null,
): string {
  const base = VIDEO_STRUCTURED_ASSET_HANDLE_BASE[category]
  return keyframeId ? `${base}::${keyframeId}` : base
}

export function parseVideoStructuredAssetHandle(
  handle: string | null | undefined,
): { category: VideoStructuredAssetCategory; keyframeId: string | null } | null {
  if (!handle) return null
  for (const category of Object.keys(VIDEO_STRUCTURED_ASSET_HANDLE_BASE) as VideoStructuredAssetCategory[]) {
    const base = VIDEO_STRUCTURED_ASSET_HANDLE_BASE[category]
    if (handle === base) return { category, keyframeId: null }
    if (handle.startsWith(`${base}::`)) return { category, keyframeId: handle.slice(base.length + 2) || null }
  }
  return null
}

export function isVideoStructuredAssetHandle(handle: string | null | undefined): boolean {
  return parseVideoStructuredAssetHandle(handle) != null
}

/** Reference Video input port (spec §P7-7/§P16/§P17) — a dedicated,
 * GLOBAL (not keyframe-scoped — a reference video conditions the whole
 * shot, not one stage) `generate_video` TARGET handle for a
 * `video_asset`/`generate_video` VIDEO-producing source. Single-slot,
 * same replace-on-reconnect semantics as START/END (never accumulates a
 * list) — see `isVideoReferenceVideoHandle` usage in `CanvasBoard.tsx`'s
 * `onConnect`. */
export const VIDEO_REFERENCE_VIDEO_HANDLE = 'reference-video-in'

export function isVideoReferenceVideoHandle(handle: string | null | undefined): boolean {
  return handle === VIDEO_REFERENCE_VIDEO_HANDLE
}

/** VistoeraTASK-00143 — 参考资产·音频 input port (spec §五/§六/§七).
 *
 * A dedicated, GLOBAL (never keyframe-scoped — audio conditions the
 * whole generation, exactly like Reference Video above, never one
 * keyframe stage) `generate_video` TARGET handle for an AUDIO-producing
 * source (`audio_asset` / `voice` / `music_sfx` / `generate_voice`).
 * UNLIKE Reference Video (single-slot, replace-on-reconnect), this is
 * MULTI-slot — spec §五 "[audio asset] [audio asset] [+]" is explicitly
 * a list, never a single START/END-style occupant — so it is handled by
 * the SAME accumulate-a-list convention `buildVideoStructuredAssetAssignment`
 * already uses for character/product/scene/reference (never added to
 * `stripConflictingGenerateVideoEdges`'s single-slot replace branch). */
export const VIDEO_AUDIO_REFERENCE_HANDLE = 'audio-reference-in'

export function isVideoAudioReferenceHandle(handle: string | null | undefined): boolean {
  return handle === VIDEO_AUDIO_REFERENCE_HANDLE
}

/** Canonical audio reference role vocabulary (spec §六) — a light,
 * OPTIONAL structured hint alongside the audio asset itself; Prompt
 * text remains the primary creative-intent surface (spec §十二 "不要往
 * Prompt 中硬塞不可见文字") — this is metadata carried in
 * `fields.audioAssetRoles`/the generation `source_context` snapshot
 * only, never rewritten into the user's editable prompt string. */
export const VIDEO_AUDIO_ROLE_VALUES = ['auto', 'bgm', 'voiceover', 'other'] as const
export type VideoAudioReferenceRole = (typeof VIDEO_AUDIO_ROLE_VALUES)[number]
export const VIDEO_AUDIO_ROLE_DEFAULT: VideoAudioReferenceRole = 'auto'

export function isVideoAudioReferenceRole(value: unknown): value is VideoAudioReferenceRole {
  return typeof value === 'string' && (VIDEO_AUDIO_ROLE_VALUES as readonly string[]).includes(value)
}

/** VISTOERA_CANVAS_GENERATE_VIDEO_EXPANDED_SEMANTIC_INPUT_HANDLES_AND_COLLAPSED_AGGREGATE_EDGE_ROUTING_P0_V1
 * Presentation-only proxy handles — NEVER persisted on `CanvasEdge.target_handle`.
 * Collapsed Generate Video nodes stack every semantic target handle at the
 * aggregate Y; this id is the ONE visible dot users drag toward. Underlying
 * edges still terminate on their real semantic handle ids. */
export const GENERATE_VIDEO_NODE_AGGREGATE_HANDLE = 'generate-video-aggregate-in'

/** Reference Assets section collapsed (node still expanded) — local visual
 * proxy for Character/Product/Scene/Other/Reference-Video handles. */
export const GENERATE_VIDEO_REFERENCE_ASSETS_AGGREGATE_HANDLE = 'reference-assets-aggregate-in'

export function isGenerateVideoPresentationAggregateHandle(handle: string | null | undefined): boolean {
  return handle === GENERATE_VIDEO_NODE_AGGREGATE_HANDLE || handle === GENERATE_VIDEO_REFERENCE_ASSETS_AGGREGATE_HANDLE
}

/** Canonical Edge Semantics
 * (VISTOERA_GENERATE_VIDEO_SEMANTIC_EDGE_BINDING_REFERENCE_IMAGE_AND_LTX_I2V_FORENSIC_P0_V1
 * §二/§三/§四) — the ONE dedicated `generate_video` TARGET handle a
 * `generate_text` source's PROMPT output is allowed to land on. Before
 * this handle existed as a real, visible, draggable drop target, a
 * manual GenerateText → GenerateVideo drag fell through to the shared
 * `GENERIC_INPUT_HANDLE` (`image-ref-in`) — the EXACT SAME port an
 * `image_asset` reference image uses — so the runtime had no semantic
 * way to distinguish "this edge is a prompt" from "this edge is a
 * reference image", and (per `videoGenerationProvider.ts` /
 * `videoVisualConditioning.ts`) nothing ever actually consumed a
 * generic TEXT input material for `generate_video` — the edge was
 * visually present but a complete no-op ("假连线"), never wired to
 * `enhancedPromptText`. Reuses the EXACT SAME wire id
 * (`enhanced-prompt-in`) the auto-created Prompt Enhancer flow
 * (`videoPromptEnhancement.ts` / `CanvasBoard.tsx`'s
 * `onCreateVideoPromptEnhancerNode`) already persists — never a second,
 * renamed handle id for the same semantic target. */
export const VIDEO_PROMPT_ENHANCEMENT_HANDLE = 'enhanced-prompt-in'

/** Original / editable prompt input — upstream TEXT binds here.
 * Distinct from `VIDEO_PROMPT_ENHANCEMENT_HANDLE` (enhancer RESULT port). */
export const VIDEO_ORIGINAL_PROMPT_IN_HANDLE = 'original-prompt-in'

/** Original / editable prompt input on `generate_image` — upstream TEXT
 * (`text_asset` / `generate_text`) binds here, never the shared reference
 * image handle (`image-ref-in`). Reuses the legacy persisted wire id
 * `prompt-in` from historical graphs (see `canvasEdgeHandleHydration.ts`). */
export const IMAGE_PROMPT_IN_HANDLE = 'prompt-in'

export function isPromptEnhancementHandle(handle: string | null | undefined): boolean {
  return handle === VIDEO_PROMPT_ENHANCEMENT_HANDLE
}

export function isOriginalPromptInputHandle(handle: string | null | undefined): boolean {
  return handle === VIDEO_ORIGINAL_PROMPT_IN_HANDLE
}

export function isImagePromptInputHandle(handle: string | null | undefined): boolean {
  return handle === IMAGE_PROMPT_IN_HANDLE
}

/** VistoeraTASK-00144 — per-asset target handle for a `generate_image`
 * reference-image edge. `GENERIC_INPUT_HANDLE` (`image-ref-in`) stays
 * the wire id EVERY reference edge is validated/created against (spec
 * §4 "canonical binding 永远不变") — this only adds a stable `::<edgeId>`
 * suffix so COLLAPSED (whole node, or just the Related Assets
 * sub-section) can mount one legacy-hidden anchor per edge at the
 * shared Result-viewport-center Y (converging visually, exactly like
 * `GENERATE_VIDEO_NODE_AGGREGATE_HANDLE`'s sibling per-category anchors),
 * while EXPANDED mounts ONE real interactive `<Handle>` per Related
 * Asset row — a genuine one-to-one target, never a shared/aggregate one.
 * `edgeId` is the Canvas edge's own persisted id (`InputMaterial.edgeId`)
 * — stable across reconnect/reorder/delete of OTHER reference edges, so
 * asset add/remove never shifts an unrelated edge's visual target (spec
 * §5 "不能因为 array index shift 导致 C 错接 B"). */
export function imageReferenceAssetHandleId(edgeId: string): string {
  return `${GENERIC_INPUT_HANDLE}::${edgeId}`
}

/** True for the bare aggregate id OR any per-edge-scoped variant — used
 * everywhere a reference-image edge's target handle needs to be
 * recognized regardless of whether it has been scoped yet (read-time
 * hydration of legacy data) or already carries its own `::<edgeId>`. */
export function isImageReferenceAssetHandle(handle: string | null | undefined): boolean {
  if (!handle) return false
  return handle === GENERIC_INPUT_HANDLE || handle.startsWith(`${GENERIC_INPUT_HANDLE}::`)
}

/** VistoeraTASK-00074 — Generate Audio structured input ports.
 *
 * Two dedicated `generate_voice` TARGET handles, one per real UI section
 * (关联资产·音频模板 / 输入文本), following the EXACT SAME precedent as
 * `IMAGE_PROMPT_IN_HANDLE`/`VIDEO_ORIGINAL_PROMPT_IN_HANDLE` above —
 * before these existed, BOTH a `generate_voice_template` output AND a
 * `text_asset`/`generate_text` output landed on the SAME shared
 * `GENERIC_INPUT_HANDLE`, so the runtime could only ever tell them apart
 * by re-inspecting the connected SOURCE node's type at read time, never
 * by the wire itself — and a manual drag had no visually distinct,
 * section-anchored drop target to aim at (root cause of the reported
 * "看不出连到哪" complaint). `GENERIC_INPUT_HANDLE` stays mounted
 * unchanged (legacy edges + the one visible collapsed drop dot); these
 * two are ADDITIVE, real DOM-owned handles rendered via
 * `VideoInputSectionHandle` inside their own `GenerateAudioNode.tsx`
 * section once expanded (never a magic pixel offset — see that
 * component's own `position:relative`/`top:50%` anchor CSS, reused
 * verbatim from the Script/Video precedent, not reinvented). */
export const AUDIO_VOICE_TEMPLATE_IN_HANDLE = 'voice-template-in'
export const AUDIO_TEXT_INPUT_HANDLE = 'audio-text-in'

export function isAudioVoiceTemplateInputHandle(handle: string | null | undefined): boolean {
  return handle === AUDIO_VOICE_TEMPLATE_IN_HANDLE
}

export function isAudioTextInputHandle(handle: string | null | undefined): boolean {
  return handle === AUDIO_TEXT_INPUT_HANDLE
}

/** Dialogue/Audio Timeline Segment slot (spec §10/§11/§22/§40) — a
 * `generate_voice`/`generate_audio` source feeding the specific
 * `DialogueSegment.id` it provides audio for. Same free-form-handle
 * trick as MIDDLE keyframes above; never a `dialogueSegment.audio =`
 * hard bind (spec §11 "UI CONTEXTUAL ≠ DATA OWNED BY KEYFRAME"). */
export const VIDEO_DIALOGUE_AUDIO_HANDLE_PREFIX = 'video-dialogue-audio-in::'

export function videoDialogueAudioHandleId(segmentId: string): string {
  return `${VIDEO_DIALOGUE_AUDIO_HANDLE_PREFIX}${segmentId}`
}

export function parseVideoDialogueAudioSegmentId(handle: string | null | undefined): string | null {
  if (!handle || !handle.startsWith(VIDEO_DIALOGUE_AUDIO_HANDLE_PREFIX)) return null
  return handle.slice(VIDEO_DIALOGUE_AUDIO_HANDLE_PREFIX.length) || null
}

export function isVideoDialogueAudioHandle(handle: string | null | undefined): boolean {
  return !!handle && handle.startsWith(VIDEO_DIALOGUE_AUDIO_HANDLE_PREFIX)
}

export const TRYON_PERSON_HANDLE = 'person-image-in'
export const TRYON_GARMENT_HANDLE = 'garment-image-in'
export const TRYON_INPUT_HANDLES = [TRYON_PERSON_HANDLE, TRYON_GARMENT_HANDLE] as const
export type TryOnInputHandle = (typeof TRYON_INPUT_HANDLES)[number]

export interface ConnectedTryOnImage {
  label: string
  ready: boolean
  entityId?: string
  generatedOutputId?: string
  tryOnOutputAssetId?: string
  tryOnOutputId?: string
}

export function isTryOnInputHandle(handle: string | null | undefined): handle is TryOnInputHandle {
  return !!handle && (TRYON_INPUT_HANDLES as readonly string[]).includes(handle)
}

/** Semantic Drop Zone target-handle compatibility
 * (VISTOERA_CONTENT_CANVAS_VIDEO_NODE_DROPZONE_PORT_MODEL_SIZE_AND_BACKGROUND_FIX_P0
 * §7 "Drop 时必须判断 source asset type"). `validateTypedConnection` above
 * only ever checked NODE-level acceptance (does `generate_video` accept
 * an IMAGE/VIDEO/AUDIO/TEXT input AT ALL) — correct for the generic
 * Reference Materials zone (`GENERIC_INPUT_HANDLE`, which really does
 * accept all four), but never distinguished "this specific target
 * *handle*" acceptance, so a VIDEO/AUDIO/TEXT source dragged onto
 * START/MIDDLE/END previously passed `onConnect` (the node-level check
 * never inspected `targetHandle`), silently creating a structurally
 * "valid" edge that `keyframeAssignmentByNodeId` (`CanvasBoard.tsx`)
 * then quietly dropped on the floor (its `sType === 'image_asset' |
 * 'generate_image'` guard just `continue`s for anything else) — a
 * dangling edge with an always-empty "空槽", never a rejection message.
 * Every KEYFRAME handle only ever accepts an IMAGE-producing source
 * (spec §6 "KEYFRAME 本质仍然是 IMAGE asset + deterministic temporal
 * role"); the four are otherwise IDENTICAL to the node-level check. */
export function validateDropZoneTarget(
  sourceType: string,
  targetType: string,
  targetHandle: string | null | undefined,
  t: TFunction<'canvas'>,
): ConnectionVerdict {
  if (isVideoKeyframeHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'IMAGE')) {
      return { ok: false, reason: t('dataflow.keyframeImageOnly') }
    }
    return { ok: true }
  }
  if (isTryOnInputHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'IMAGE')) {
      return { ok: false, reason: t('dataflow.tryonImageOnly') }
    }
    return { ok: true }
  }
  // Structured Asset ports (character/product/scene/reference-asset) —
  // spec §P7 items 2-5: IMAGE-producing sources only, identical rule to
  // KEYFRAME above (an image_asset/generate_image reference), never a
  // VIDEO/TEXT/AUDIO source silently accepted into a slot the Detail
  // Panel can only ever render as an image chip.
  if (isVideoStructuredAssetHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'IMAGE')) {
      return { ok: false, reason: t('dataflow.keyframeImageOnly') }
    }
    return { ok: true }
  }
  // Reference Video port (spec §P7 item 7/§P16) — VIDEO-producing
  // sources only (`video_asset`/`generate_video`).
  if (isVideoReferenceVideoHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'VIDEO')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'VIDEO') }),
      }
    }
    return { ok: true }
  }
  // Audio reference port (VistoeraTASK-00143 §五/§七) — AUDIO-producing
  // sources only (`audio_asset`/`voice`/`music_sfx`/`generate_voice`),
  // same "one dedicated port, one dedicated data type" discipline as
  // Reference Video above.
  if (isVideoAudioReferenceHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'AUDIO')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'AUDIO') }),
      }
    }
    return { ok: true }
  }
  // Prompt Enhancement drop zone (§二/§三/§四) — ONLY a TEXT-producing
  // source (`generate_text`) may land here; an IMAGE/VIDEO/AUDIO source
  // dropped exactly on this handle is rejected with the same "type
  // incompatible" message the generic port would give, rather than
  // silently accepting it into a slot whose sole consumer
  // (`enhancedPromptText`) only ever reads a string.
  if (targetType === 'generate_video' && isPromptEnhancementHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'TEXT')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
      }
    }
    return { ok: true }
  }
  if (targetType === 'generate_video' && isOriginalPromptInputHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'TEXT')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
      }
    }
    return { ok: true }
  }
  if (targetType === 'generate_image' && isImagePromptInputHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'TEXT')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
      }
    }
    return { ok: true }
  }
  // Generate Audio structured ports (VistoeraTASK-00074) — a
  // `generate_voice_template` VOICE_TEMPLATE-producing source only for
  // the 关联资产 port, a TEXT-producing source only for the 输入文本 port.
  // Mirrors `isImagePromptInputHandle`/`isOriginalPromptInputHandle`
  // above verbatim — the ONE dedicated port a given data type may land
  // on, rejected with the same "type incompatible" message otherwise.
  if (targetType === 'generate_voice' && isAudioVoiceTemplateInputHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'VOICE_TEMPLATE')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'VOICE_TEMPLATE') }),
      }
    }
    return { ok: true }
  }
  if (targetType === 'generate_voice' && isAudioTextInputHandle(targetHandle)) {
    const sourceIO = getNodeIO(sourceType)
    if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'TEXT')) {
      return {
        ok: false,
        reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
      }
    }
    return { ok: true }
  }
  // VistoeraTASK-00144 — `isImageReferenceAssetHandle` also matches a
  // brand-new connection dropped directly onto an EXPANDED Related
  // Asset row's already-scoped `image-ref-in::<edgeId>` handle (a real,
  // independently-connectable DOM `<Handle>`), never just the bare id.
  if (targetType === 'generate_image' && (targetHandle == null || isImageReferenceAssetHandle(targetHandle))) {
    const sourceIO = getNodeIO(sourceType)
    const isTextOnlySource = !!sourceIO && sourceIO.outputs.length > 0 && sourceIO.outputs.every((o) => o.dataType === 'TEXT')
    if (isTextOnlySource) {
      return { ok: false, reason: t('dataflow.imageTextRequiresPromptPort') }
    }
  }
  if (targetType === 'script_generation' && isScriptSourceInputHandle(targetHandle)) {
    if (sourceType === 'text_asset' || sourceType === 'generate_text') {
      const sourceIO = getNodeIO(sourceType)
      if (!sourceIO || !sourceIO.outputs.some((o) => o.dataType === 'TEXT')) {
        return {
          ok: false,
          reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
        }
      }
      return { ok: true }
    }
    if (sourceType === 'generate_creative' || sourceType === 'creative_candidate') {
      return { ok: true }
    }
    return {
      ok: false,
      reason: t('dataflow.typeIncompatible', { types: dataTypeLabel(t, 'TEXT') }),
    }
  }
  // Explicit Target Input model
  // (VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
  // §一/§四/§八) — an IMAGE-only source (`image_asset`/`generate_image`)
  // may no longer land on `generate_video`'s shared
  // `GENERIC_INPUT_HANDLE` (the old "参考素材" catch-all tray, historically
  // anchored near the top media body — root cause of images silently
  // NOT reaching Character/Product/Scene/Other Reference). The user
  // must pick one of the SIX explicit ports (keyframe/character/
  // product/scene/other-reference/reference-video) instead — never a
  // silent `other_reference` downgrade (§八 "不要静默把它变成
  // other_reference"). Non-IMAGE sources (TEXT/AUDIO/VIDEO/MOTION_ASSET
  // general context materials) are UNAFFECTED — this rule only ever
  // narrows the IMAGE case, so pre-existing non-image "参考素材" tray
  // edges keep working exactly as before.
  if (targetType === 'generate_video' && (targetHandle == null || targetHandle === GENERIC_INPUT_HANDLE)) {
    const sourceIO = getNodeIO(sourceType)
    const isImageOnlySource = !!sourceIO && sourceIO.outputs.length > 0 && sourceIO.outputs.every((o) => o.dataType === 'IMAGE')
    if (isImageOnlySource) {
      return { ok: false, reason: t('dataflow.videoImageRequiresExplicitPort') }
    }
  }
  return validateTypedConnection(sourceType, targetType, t)
}

export interface NodeOutputPort {
  handleId: string
  dataType: DataType
}

export interface NodeIOSpec {
  /** `null` = Source/Asset node — provides output only, and MUST NEVER
   * be a legal target for a new connection (§2A "Source Node 不应该被当
   * 作 Generation Node 的执行结果插槽"). A node with a visually-present
   * but semantically-inert target `<Handle>` (kept for P1 left/right
   * geometry symmetry — see `ImageAssetNode.tsx`) still reports `null`
   * here; `validateConnection` is what actually enforces the rejection,
   * the DOM handle's mere presence never implies validity. */
  inputHandleId: string | null
  acceptedInputTypes: DataType[]
  outputs: NodeOutputPort[]
}

const NO_INPUT: Pick<NodeIOSpec, 'inputHandleId' | 'acceptedInputTypes'> = {
  inputHandleId: null,
  acceptedInputTypes: [],
}

/** Every node type this round's typed contract is actually formalized
 * for (§2/§5/§6/§7 — the P0-required IMAGE/TEXT/VIDEO/AUDIO Source +
 * Generation set). Any node type NOT in this map (Scene, Character,
 * Dialogue, Virtual IP, generate_production_plan, publish, ...) is
 * deliberately left OUT of the typed-dataflow contract this round —
 * §54 "不要扩大范围" — and keeps falling back to the OLD, narrower
 * `LEGACY_RAW_INPUT_TYPES` heuristic in `CanvasBoard.tsx`, unchanged. */
const NODE_IO_REGISTRY: Record<string, NodeIOSpec> = {
  // ---- Source / Asset nodes (§2A) — OUTPUT only ----
  image_asset: { ...NO_INPUT, outputs: [{ handleId: 'image-out', dataType: 'IMAGE' }] },
  text_asset: { ...NO_INPUT, outputs: [{ handleId: 'text-out', dataType: 'TEXT' }] },
  video_asset: { ...NO_INPUT, outputs: [{ handleId: 'video-out', dataType: 'VIDEO' }] },
  voice: { ...NO_INPUT, outputs: [{ handleId: 'audio-out', dataType: 'AUDIO' }] },
  music_sfx: { ...NO_INPUT, outputs: [{ handleId: 'audio-out', dataType: 'AUDIO' }] },
  // VistoeraTASK-00115 — canonical "音频资产" Asset Node. Same AUDIO output
  // contract as the pre-existing `voice`/`music_sfx` entity nodes above
  // (never a second data type), but backed by a real canonical SiteAsset
  // (`AudioAssetPicker`) instead of a label-only entity. Deliberately
  // AUDIO, never VOICE_TEMPLATE — `generate_voice`'s `acceptedInputTypes`
  // below is `['TEXT', 'VOICE_TEMPLATE']` (no AUDIO), so an `audio_asset`
  // source landing on `generate_voice`'s GENERIC_INPUT_HANDLE is correctly
  // REJECTED by `validateTypedConnection` — Audio Asset must never be
  // usable as a Voice Template (spec §P48/§P49). It DOES legally reach
  // `generate_video`'s generic reference handle, which already accepts
  // AUDIO (Canvas intent-layer acceptance, unchanged by this task).
  audio_asset: { ...NO_INPUT, outputs: [{ handleId: 'audio-out', dataType: 'AUDIO' }] },
  motion_asset: { ...NO_INPUT, outputs: [{ handleId: 'motion-out', dataType: 'MOTION_ASSET' }] },

  // ---- Processor / Generation nodes (§2B) — INPUT + OUTPUT ----
  // Accepted input sets follow §6's own worked example verbatim; VIDEO/
  // AUDIO marked "*" there ("Canvas intent layer" accepted, Execution
  // Layer decides consume/convert/reject) are still listed here as
  // ACCEPTED for connection-graph purposes — §6 "不要把 Provider 限制硬编
  // 码到 Canvas 图结构" is precisely why this file never asks "does the
  // FLUX/Kontext/LTX worker actually support this input" before
  // allowing the edge to exist.
  generate_image: {
    inputHandleId: GENERIC_INPUT_HANDLE,
    acceptedInputTypes: ['TEXT', 'IMAGE', 'VIDEO'],
    outputs: [{ handleId: 'image-out', dataType: 'IMAGE' }],
  },
  generate_video: {
    inputHandleId: GENERIC_INPUT_HANDLE,
    // MOTION_ASSET: Canvas intent-layer context hop from motion_reconstruction
    // (legal edge + field bind). Execution Layer decides consume/convert —
    // never auto-submit a video job from the Quick Action alone.
    //
    // TEXT is deliberately NOT in this generic-handle set (P0 Canonical
    // Edge Semantics §二/§三) — a `generate_text` PROMPT source only
    // ever lands on the dedicated `VIDEO_PROMPT_ENHANCEMENT_HANDLE` (see
    // `isPromptEnhancementHandle` above), never the shared reference
    // handle a reference IMAGE also uses. Before this, TEXT was
    // accepted here too, so the target *handle* for both an image and a
    // prompt was IDENTICAL and nothing downstream ever actually read a
    // generic TEXT input material for `generate_video` — a visually
    // real but functionally dead edge.
    acceptedInputTypes: ['IMAGE', 'VIDEO', 'AUDIO', 'MOTION_ASSET'],
    outputs: [{ handleId: 'video-out', dataType: 'VIDEO' }],
  },
  generate_text: {
    inputHandleId: GENERIC_INPUT_HANDLE,
    acceptedInputTypes: ['IMAGE', 'TEXT'],
    outputs: [{ handleId: 'text-out', dataType: 'TEXT' }],
  },
  generate_voice: {
    inputHandleId: GENERIC_INPUT_HANDLE,
    acceptedInputTypes: ['TEXT', 'VOICE_TEMPLATE'],
    outputs: [{ handleId: 'audio-out', dataType: 'AUDIO' }],
  },
  generate_voice_template: {
    inputHandleId: GENERIC_INPUT_HANDLE,
    acceptedInputTypes: ['TEXT', 'AUDIO'],
    outputs: [{ handleId: 'voice-template-out', dataType: 'VOICE_TEMPLATE' }],
  },
  motion_reconstruction: {
    inputHandleId: 'video-in',
    acceptedInputTypes: ['VIDEO'],
    outputs: [{ handleId: 'motion-out', dataType: 'MOTION_ASSET' }],
  },
  generate_tryon: {
    inputHandleId: null,
    acceptedInputTypes: ['IMAGE'],
    outputs: [{ handleId: 'image-out', dataType: 'IMAGE' }],
  },
}

/** Node types participating in the typed-dataflow contract at all
 * (either side of a connection). Anything else always falls through to
 * the pre-existing legacy gate untouched. */
export const TYPED_DATAFLOW_NODE_TYPES = new Set(Object.keys(NODE_IO_REGISTRY))

export function getNodeIO(nodeType: string): NodeIOSpec | null {
  return NODE_IO_REGISTRY[nodeType] ?? null
}

export function nodeOutputDataTypes(nodeType: string): DataType[] {
  return getNodeIO(nodeType)?.outputs.map((o) => o.dataType) ?? []
}

export interface ConnectionVerdict {
  ok: boolean
  reason?: string
}

/** The one general rule (§4) replacing every old node-type-pair special
 * case. Only called when BOTH sides participate in the typed contract
 * (`TYPED_DATAFLOW_NODE_TYPES`) — see `CanvasBoard.tsx`'s `onConnect`
 * for the dispatch between this and the legacy gate. */
export function validateTypedConnection(sourceType: string, targetType: string, t: TFunction<'canvas'>): ConnectionVerdict {
  const sourceIO = getNodeIO(sourceType)
  const targetIO = getNodeIO(targetType)
  if (!sourceIO || sourceIO.outputs.length === 0) {
    return { ok: false, reason: t('dataflow.noOutput') }
  }
  if (!targetIO || targetIO.inputHandleId === null) {
    return { ok: false, reason: t('dataflow.assetNotTarget') }
  }
  const sourceTypes = sourceIO.outputs.map((o) => o.dataType)
  const compatible = sourceTypes.some((dt) => targetIO.acceptedInputTypes.includes(dt))
  if (!compatible) {
    return {
      ok: false,
      reason: t('dataflow.typeIncompatible', {
        types: sourceTypes.map((dt) => dataTypeLabel(t, dt)).join('/'),
      }),
    }
  }
  return { ok: true }
}

/** Cycle Policy (§22) — "阻止 generation dependency cycle". Applied
 * generally (any edge, not only typed-contract node types) since a
 * literal A→B→C→A loop is nonsensical everywhere in this DAG-shaped
 * Canvas and no existing feature intentionally relies on one. Answers
 * "if I add `newSource -> newTarget`, does `newTarget` already have a
 * path back to `newSource`?" via a plain BFS over existing edges —
 * O(V+E), trivial at the 50-node/100-edge scale §36 requires. */
export function wouldCreateCycle(
  edges: Array<{ source: string; target: string }>,
  newSource: string,
  newTarget: string,
): boolean {
  if (newSource === newTarget) return true
  const adjacency = new Map<string, string[]>()
  for (const e of edges) {
    const list = adjacency.get(e.source)
    if (list) list.push(e.target)
    else adjacency.set(e.source, [e.target])
  }
  // BFS forward from newTarget — if newSource is reachable, the new
  // edge would close a loop.
  const queue = [newTarget]
  const seen = new Set<string>([newTarget])
  while (queue.length > 0) {
    const current = queue.shift() as string
    if (current === newSource) return true
    for (const next of adjacency.get(current) || []) {
      if (seen.has(next)) continue
      seen.add(next)
      queue.push(next)
    }
  }
  return false
}

/** Duplicate Edge Policy (§21) — an identical (source, target,
 * sourceHandle, targetHandle) tuple already exists. Mirrors the
 * backend's own dedup key in `production_canvas_service.save_graph`
 * (`(source_id, target_id, source_handle, target_handle)`), so the
 * frontend now rejects up front instead of silently losing a
 * "duplicate" edge only at the next save round-trip. */
export function isDuplicateEdge(
  edges: Array<{ source: string; target: string; sourceHandle?: string | null; targetHandle?: string | null }>,
  candidate: { source: string; target: string; sourceHandle?: string | null; targetHandle?: string | null },
): boolean {
  return edges.some(
    (e) =>
      e.source === candidate.source &&
      e.target === candidate.target &&
      (e.sourceHandle ?? null) === (candidate.sourceHandle ?? null) &&
      (e.targetHandle ?? null) === (candidate.targetHandle ?? null),
  )
}

// ---------------------------------------------------------------------
// Unified Input Material (§9–§15) — what a Generation Node's "输入素材"
// section actually renders, derived fresh from the live edge graph
// every render (never persisted itself — see `CanvasBoard.tsx`'s
// `inputMaterialsByNodeId`), generalizing the old, image-only
// `referenceNodes: Array<{nodeId, entityId}>` to every data type this
// round introduces.
// ---------------------------------------------------------------------

export interface InputMaterialBase {
  /** Canvas edge id — lets the UI disconnect EXACTLY this material's
   * edge without guessing from `nodeId` alone (a source node could in
   * principle feed the same target twice via different handles). */
  edgeId: string
  nodeId: string
  nodeType: string
  /** §15 "source_node_id / source_output / source_asset_id" — kept
   * explicit on every material entry (not just re-derivable from
   * `nodeId`) so "查看来源"/highlight-source UI never needs a second
   * graph walk. */
  sourceLabel: string
  inputRole: InputRole
}

export interface ImageInputMaterial extends InputMaterialBase {
  kind: 'IMAGE'
  ready: boolean
  /** `image_asset` source → SiteAsset id. */
  entityId?: string
  /** `generate_image` source → its own completed output id (§7/§8 —
   * gen-to-gen; §19 "output_asset_id"). */
  generatedOutputId?: string
}

export interface TextInputMaterial extends InputMaterialBase {
  kind: 'TEXT'
  ready: boolean
  text?: string
}

export interface VideoInputMaterial extends InputMaterialBase {
  kind: 'VIDEO'
  ready: boolean
  url?: string
}

/** Canvas Unified Video Generation Node P0 (spec §6/§21) — `voice`/
 * `music_sfx` Source nodes feeding a `generate_video` node's generic
 * Input Assets area. No real waveform engine this round (spec §21 "不
 * 要为了这轮新造复杂 waveform engine") — a compact icon + optional label is
 * enough; `durationSeconds` is `undefined` until a real backing asset
 * exposes one (never fabricated). */
export interface AudioInputMaterial extends InputMaterialBase {
  kind: 'AUDIO'
  ready: boolean
  durationSeconds?: number
}

export type InputMaterial = ImageInputMaterial | TextInputMaterial | VideoInputMaterial | AudioInputMaterial

// ---------------------------------------------------------------------
// Keyframe Assignment (§7–§9) — computed the SAME way as
// `InputMaterial` (fresh from the live edge graph every render, never
// persisted itself), but keyed by KEYFRAME ROLE instead of accumulated
// into one flat list — `generate_video`'s Keyframe Area needs exactly
// "what (if anything) is in START", not a list to search through.
// ---------------------------------------------------------------------

export interface KeyframeSlotMaterial {
  edgeId: string
  nodeId: string
  nodeType: string
  sourceLabel: string
  ready: boolean
  /** `image_asset` source → SiteAsset id. Reused by the reference-video
   * slot (spec §P16/§P17) for an upload-sourced `video_asset` source →
   * already-stripped `VideoGenerationAsset.id` (see
   * `buildVideoReferenceVideoAssignment`). */
  entityId?: string
  /** `generate_image` source → its own completed output id (gen-to-gen). */
  generatedOutputId?: string
  /** Only ever set for MIDDLE slots (spec §9). */
  timePosition?: number
}


// ---------------------------------------------------------------------
// Stable Asset Alias (spec §11) — "图片1/图片2/视频1/音频1/文字1", computed
// fresh every render from the live Input Materials list (never
// persisted), but keyed by `edgeId` so identity survives a re-render
// even if the underlying list order shifts (spec §11 "UI 排序变化不能破坏
// 底层引用...底层必须引用 asset_id/node_id/edge identity" — the NUMBER may
// re-flow if an earlier same-kind material disconnects, but every
// Asset Mention in a Prompt always resolves through `edgeId`, never
// the display string alone).
// ---------------------------------------------------------------------

export function computeMaterialAliases(materials: InputMaterial[], t: TFunction<'canvas'>): Map<string, string> {
  const aliasPrefix: Record<InputMaterial['kind'], string> = {
    IMAGE: t('dataflow.aliasImage'),
    VIDEO: t('dataflow.aliasVideo'),
    AUDIO: t('dataflow.aliasAudio'),
    TEXT: t('dataflow.aliasText'),
  }
  const counters: Record<string, number> = { IMAGE: 0, VIDEO: 0, AUDIO: 0, TEXT: 0 }
  const out = new Map<string, string>()
  for (const m of materials) {
    counters[m.kind] += 1
    out.set(m.edgeId, `${aliasPrefix[m.kind]}${counters[m.kind]}`)
  }
  return out
}

export interface KeyframeAssignment {
  start?: KeyframeSlotMaterial
  /** Legacy accumulator — every MIDDLE slot regardless of which entry it
   * belongs to (kept so any pre-existing single-MIDDLE consumer code
   * keeps working unchanged: `middle[0]` is still "the first/only
   * MIDDLE" for old canvases). New N-keyframe UI must read
   * `middleByEntryId` instead (spec §4 "禁止硬编码 START+MID+END"). */
  middle: KeyframeSlotMaterial[]
  end?: KeyframeSlotMaterial
  /** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4/§40 —
   * one `KeyframeSlotMaterial` per MIDDLE `KeyframeTimelineEntry.id`,
   * parsed from the `video-kf-middle-in::<entryId>` handle suffix (see
   * `parseVideoKeyframeMiddleEntryId` above). Populated alongside
   * `middle` in `CanvasBoard.tsx`'s `keyframeAssignmentByNodeId` —
   * never a second independent derivation. */
  middleByEntryId: Map<string, KeyframeSlotMaterial>
  /** The pre-existing bare `video-kf-middle-in` slot (no `::entryId`
   * suffix) — i.e. an edge created before this round. Distinct from
   * `middleByEntryId` only so `legacyTimelineFromAssignment` can tell
   * "this canvas already had a MIDDLE edge before it ever had a
   * `keyframeTimeline` field" and synthesize exactly one legacy MIDDLE
   * timeline entry for it once, instead of silently losing the edge. */
  legacyMiddle?: KeyframeSlotMaterial
}

// ---------------------------------------------------------------------
// Structured Asset Port Assignment (spec §P6/§P7/§P15/§P19) — the SAME
// "derive fresh from the live edge graph every render, never persisted"
// pattern as `KeyframeAssignment` above, generalized to the four
// character/product/scene/reference-asset ports. EDGE ⇔ BINDING is
// automatic here (no field write on connect, no field cleanup on
// delete needed) — removing the edge alone already removes exactly
// that one entry from this projection on the next render, satisfying
// §P15's "delete edge only removes THIS binding" without a second
// explicit cleanup code path. Coexists with (never replaces) the
// pre-existing `fields.inputAssetRefs` picker-added refs — callers
// (`GenerateVideoNode.tsx`) merge both lists for display; this module
// only ever derives the EDGE half.
//
// A plain-object node/edge shape (not `@xyflow/react`'s `Node`/`Edge`)
// is used deliberately — this file has never imported those types
// (`wouldCreateCycle`/`isDuplicateEdge` above are the existing
// precedent) so it stays trivially unit-testable with plain fixtures.
// ---------------------------------------------------------------------

export interface StructuredAssetEdgeLike {
  id: string
  source: string
  target: string
  targetHandle?: string | null
}

export interface StructuredAssetNodeLike {
  id: string
  data: { nodeType?: string; fields?: Record<string, unknown>; entityId?: string | null }
}

/** Keying convention shared by the builder and its reader
 * (`structuredAssetSlotsFor`) — `${category}::${keyframeId ?? '__global__'}`.
 * `'__global__'` is the legacy/no-keyframe-timeline-yet bucket, mirroring
 * `videoStructuredAssetHandleId`'s own bare-handle (no `::id` suffix)
 * convention for that same case. */
export function structuredAssetAssignmentKey(
  category: VideoStructuredAssetCategory,
  keyframeId: string | null | undefined,
): string {
  return `${category}::${keyframeId ?? '__global__'}`
}

/** One slot material per (source, category, keyframe) edge — a
 * category/keyframe pair can legitimately hold SEVERAL assets (spec §P7
 * "角色/产品/场景/其他参考" chips are a list, never a single START/END-style
 * slot), so unlike `KeyframeAssignment.start`/`.end` this accumulates a
 * list per key rather than replacing. */
export function buildVideoStructuredAssetAssignment(
  edges: StructuredAssetEdgeLike[],
  nodes: StructuredAssetNodeLike[],
): Map<string, Map<string, KeyframeSlotMaterial[]>> {
  const byTargetNode = new Map<string, Map<string, KeyframeSlotMaterial[]>>()
  for (const e of edges) {
    const parsed = parseVideoStructuredAssetHandle(e.targetHandle)
    if (!parsed) continue
    const sourceNode = nodes.find((n) => n.id === e.source)
    if (!sourceNode) continue
    const sType = sourceNode.data.nodeType || ''
    const sourceLabel = (sourceNode.data.fields?.label as string | undefined) || sType
    let slot: KeyframeSlotMaterial | null = null
    if (sType === 'image_asset' && sourceNode.data.entityId) {
      slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready: true, entityId: sourceNode.data.entityId }
    } else if (sType === 'generate_image') {
      const outputId = sourceNode.data.fields?.generatedOutputId as string | undefined
      const formatOk = (sourceNode.data.fields?.formatCompatibility as string | undefined) !== 'format_mismatch'
      slot = {
        edgeId: e.id,
        nodeId: sourceNode.id,
        nodeType: sType,
        sourceLabel,
        ready: Boolean(outputId) && formatOk,
        generatedOutputId: formatOk ? outputId : undefined,
      }
    }
    if (!slot) continue
    const key = structuredAssetAssignmentKey(parsed.category, parsed.keyframeId)
    let byKey = byTargetNode.get(e.target)
    if (!byKey) {
      byKey = new Map<string, KeyframeSlotMaterial[]>()
      byTargetNode.set(e.target, byKey)
    }
    const list = byKey.get(key) || []
    list.push(slot)
    byKey.set(key, list)
  }
  return byTargetNode
}

export function structuredAssetSlotsFor(
  assignment: Map<string, KeyframeSlotMaterial[]> | undefined,
  category: VideoStructuredAssetCategory,
  keyframeId: string | null | undefined,
): KeyframeSlotMaterial[] {
  if (!assignment) return []
  return assignment.get(structuredAssetAssignmentKey(category, keyframeId)) || []
}

/** Canonical Generation Payload preservation (spec §十五 "Generation
 * Payload 必须最终验证") — a plain, JSON-safe snapshot of every
 * structured semantic category CURRENTLY bound (via edge) for one
 * keyframe scope, written verbatim into `CreateTaskRequest.source_context`
 * (an already-existing free-form dict — see `saas_video_generation.py`)
 * so Character/Product/Scene/Other-Reference/Reference-Video semantic
 * distinction survives all the way to task creation, never silently
 * flattened before it is even persisted.
 *
 * HONEST SCOPE (do not over-claim): today's real generation EXECUTION
 * still only ever reads the single flat `reference_image_asset_id` /
 * `reference_video_asset_id` scalar fields (no provider adapter
 * consumes a multi-image category list yet) — this snapshot is
 * diagnostic/forward-compatible plumbing, the SAME honesty pattern
 * `buildVideoReferenceVideoAssignment`'s `ready:false` case already
 * uses for a connected-but-not-yet-submittable slot. */
export interface StructuredAssetContextEntry {
  source_label: string
  entity_id?: string
  generated_output_id?: string
  ready: boolean
}

export interface StructuredAssetContextSnapshot {
  characters: StructuredAssetContextEntry[]
  products: StructuredAssetContextEntry[]
  scenes: StructuredAssetContextEntry[]
  other_references: StructuredAssetContextEntry[]
  reference_video: StructuredAssetContextEntry | null
  /** VistoeraTASK-00143 §七 — canonical audio structured context. Additive
   * only; absent on any Canvas persisted before this task (spec §十四
   * backward compatibility — every reader here treats a missing/empty
   * list identically to "no audio connected", never a required field). */
  audio_references: AudioReferenceContextEntry[]
}

function structuredAssetContextEntry(slot: KeyframeSlotMaterial): StructuredAssetContextEntry {
  return {
    source_label: slot.sourceLabel,
    entity_id: slot.entityId,
    generated_output_id: slot.generatedOutputId,
    ready: slot.ready,
  }
}

export interface AudioReferenceContextEntry extends StructuredAssetContextEntry {
  role: VideoAudioReferenceRole
}

function audioReferenceContextEntry(
  slot: KeyframeSlotMaterial,
  roleByEdgeId: Record<string, unknown> | undefined,
): AudioReferenceContextEntry {
  const rawRole = roleByEdgeId?.[slot.edgeId]
  return {
    ...structuredAssetContextEntry(slot),
    role: isVideoAudioReferenceRole(rawRole) ? rawRole : VIDEO_AUDIO_ROLE_DEFAULT,
  }
}

export function buildStructuredAssetContextSnapshot(
  assignment: Map<string, KeyframeSlotMaterial[]> | undefined,
  keyframeId: string | null | undefined,
  referenceVideoSlot: KeyframeSlotMaterial | null | undefined,
  audioSlots?: KeyframeSlotMaterial[],
  audioRoleByEdgeId?: Record<string, unknown>,
): StructuredAssetContextSnapshot {
  return {
    characters: structuredAssetSlotsFor(assignment, 'character', keyframeId).map(structuredAssetContextEntry),
    products: structuredAssetSlotsFor(assignment, 'object', keyframeId).map(structuredAssetContextEntry),
    scenes: structuredAssetSlotsFor(assignment, 'scene', keyframeId).map(structuredAssetContextEntry),
    other_references: structuredAssetSlotsFor(assignment, 'reference', keyframeId).map(structuredAssetContextEntry),
    reference_video: referenceVideoSlot ? structuredAssetContextEntry(referenceVideoSlot) : null,
    audio_references: (audioSlots || []).map((slot) => audioReferenceContextEntry(slot, audioRoleByEdgeId)),
  }
}

/** `video_asset` Canvas node `entityId` prefix for an upload-sourced video
 * asset library item (see `canvasVideoAssetResolve.ts` `videoAssetItemFrom
 * Binding` — the ONLY `video_asset` source kind whose id is already a real
 * `VideoGenerationAsset` row, because `saas_video_assets.py`'s upload route
 * reuses `video_generation_assets.upload_direct` verbatim for the write).
 * Job-sourced (`job:`) and Composite-sourced (`composite:`) library items
 * are Job/Composite outputs, NOT `VideoGenerationAsset` rows — there is no
 * backend bridge (unlike images' universal `materialize_site_asset`) that
 * turns one into a `reference_video_asset_id`-eligible row yet. */
const VIDEO_ASSET_UPLOAD_ENTITY_PREFIX = 'upload:'

/** Reference Video Port Assignment (spec §P16/§P17) — single-slot, same
 * replace-on-reconnect semantics as `KeyframeAssignment.start`/`.end`
 * (`CanvasBoard.tsx`'s `onConnect` filters any prior edge on this exact
 * handle before adding the new one, mirroring `isVideoKeyframeHandle`).
 *
 * `video_asset` sources are ONLY `ready` when their `entityId` is upload-
 * sourced (`upload:<VideoGenerationAsset.id>` — already a real row in the
 * exact table `reference_video_asset_id` is validated against, no
 * materialize call needed, see `video_generation_canvas_bridge.py`'s own
 * docstring for why `SiteAsset`/`VideoGenerationAsset` are deliberately
 * separate tables). Job-sourced / Composite-sourced `video_asset` items,
 * and `generate_video` sources (GENERATED_VIDEO), are intentionally NEVER
 * marked `ready`/materializable here — the Canvas binding exists (edge +
 * this projection), but no backend bridge from a video-generation-task or
 * continuation-composite OUTPUT to a `VideoGenerationAsset` row exists yet
 * (only `'site_asset'` / `'image_generation_output'` `materialize-asset`
 * `kind`s do, and neither applies to a video output). Reporting `ready:
 * true` for those cases would be the exact "假装已经能送给 Seedance"
 * §P16/§P17 forbids — the UI must show a truthful "已连接·暂不支持提交"
 * state instead of silently dropping or silently claiming it. */
export function buildVideoReferenceVideoAssignment(
  edges: StructuredAssetEdgeLike[],
  nodes: StructuredAssetNodeLike[],
): Map<string, KeyframeSlotMaterial> {
  const map = new Map<string, KeyframeSlotMaterial>()
  for (const e of edges) {
    if (!isVideoReferenceVideoHandle(e.targetHandle)) continue
    const sourceNode = nodes.find((n) => n.id === e.source)
    if (!sourceNode) continue
    const sType = sourceNode.data.nodeType || ''
    const sourceLabel = (sourceNode.data.fields?.label as string | undefined) || sType
    let slot: KeyframeSlotMaterial | null = null
    if (sType === 'video_asset' && sourceNode.data.entityId) {
      const entityId = sourceNode.data.entityId
      const isUploadSourced = entityId.startsWith(VIDEO_ASSET_UPLOAD_ENTITY_PREFIX)
      slot = {
        edgeId: e.id,
        nodeId: sourceNode.id,
        nodeType: sType,
        sourceLabel,
        ready: isUploadSourced,
        entityId: isUploadSourced ? entityId.slice(VIDEO_ASSET_UPLOAD_ENTITY_PREFIX.length) : entityId,
      }
    } else if (sType === 'generate_video') {
      slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready: false }
    }
    if (!slot) continue
    map.set(e.target, slot)
  }
  return map
}

/** Audio Reference Port Assignment (VistoeraTASK-00143 §五/§七) —
 * MULTI-slot (spec §五 "[audio asset] [audio asset] [+]" is a list),
 * accumulates one `KeyframeSlotMaterial` per edge landing on
 * `VIDEO_AUDIO_REFERENCE_HANDLE`, mirroring
 * `buildVideoStructuredAssetAssignment`'s own accumulate-a-list
 * convention rather than `buildVideoReferenceVideoAssignment`'s
 * single-slot replace.
 *
 * `ready` truthfulness mirrors this file's existing per-source-type
 * rules exactly (never invents a stronger guarantee for audio than the
 * identical source type already gets elsewhere in this module):
 *  - `audio_asset` (canonical SiteAsset-backed node, VistoeraTASK-00115)
 *    — ready the moment its `entityId` exists, same as `image_asset`.
 *  - `voice`/`music_sfx` (label-only entity nodes) — ready the moment
 *    the edge exists, same as `CanvasBoard.tsx`'s own
 *    `inputMaterialsByNodeId` treatment of these two types.
 *  - `generate_voice` — ready only once its OWN generation actually
 *    completed with a real `audioAssetId` (never "假装已经生成好了"). */
export function buildVideoAudioReferenceAssignment(
  edges: StructuredAssetEdgeLike[],
  nodes: StructuredAssetNodeLike[],
): Map<string, KeyframeSlotMaterial[]> {
  const map = new Map<string, KeyframeSlotMaterial[]>()
  for (const e of edges) {
    if (!isVideoAudioReferenceHandle(e.targetHandle)) continue
    const sourceNode = nodes.find((n) => n.id === e.source)
    if (!sourceNode) continue
    const sType = sourceNode.data.nodeType || ''
    const sourceLabel = (sourceNode.data.fields?.label as string | undefined) || sType
    let slot: KeyframeSlotMaterial | null = null
    if (sType === 'audio_asset' && sourceNode.data.entityId) {
      slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready: true, entityId: sourceNode.data.entityId }
    } else if (sType === 'voice' || sType === 'music_sfx') {
      slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready: true }
    } else if (sType === 'generate_voice') {
      const assetId = sourceNode.data.fields?.audioAssetId as string | undefined
      const ready = sourceNode.data.fields?.status === 'COMPLETED' && Boolean(assetId) && !sourceNode.data.fields?.needsRegeneration
      slot = { edgeId: e.id, nodeId: sourceNode.id, nodeType: sType, sourceLabel, ready, entityId: ready ? assetId : undefined }
    }
    if (!slot) continue
    const list = map.get(e.target) || []
    list.push(slot)
    map.set(e.target, list)
  }
  return map
}

/** ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §10/§11/§22/§40
 * — one audio slot per `DialogueSegment.id`, computed the exact same way
 * as `KeyframeAssignment.middleByEntryId` from
 * `video-dialogue-audio-in::<segmentId>` edges. This is purely a UI
 * "what's currently wired in" projection — the segment's OWN
 * `audioAssetId` (if hydrated from a Shot or already generated) is the
 * data of record; this map only reflects a live, not-yet-materialized
 * Canvas edge to a `generate_voice`/`generate_audio` node (spec §11
 * "UI CONTEXTUAL ≠ DATA OWNED BY KEYFRAME"). */
export interface DialogueAudioSlotMaterial {
  edgeId: string
  nodeId: string
  nodeType: string
  sourceLabel: string
  ready: boolean
  audioAssetId?: string
}

export type DialogueAudioAssignment = Map<string, DialogueAudioSlotMaterial>
